import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import Reveal from "../components/Reveal";
import { WhatsAppCTA } from "../components/Buttons";
import { PhoneIconBtn, MailIconBtn } from "../components/ContactIcons";
import { SITE } from "../lib/site";
import useSEO from "../hooks/useSEO";

const MSME_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Service",
  "serviceType": "MSME Payment Recovery",
  "provider": { "@id": "https://www.svmd.in/#org" },
  "areaServed": { "@type": "Country", "name": "India" },
  "name": "MSME Payment Recovery & Dispute Resolution",
  "description":
    "Recovery of delayed payments under the MSME Development Act including MSME Samadhaan filings, interest computation under Section 16, structuring of claims, and end-to-end handling of recovery proceedings.",
  "url": "https://www.svmd.in/msme-recovery",
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "MSME recovery support",
    "itemListElement": [
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "MSME eligibility review" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "MSME Samadhaan application filing" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Interest computation on delayed payments (Section 16, MSMED Act)" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Documentation and claim structuring" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Coordination with legal counsel and follow-through" } }
    ]
  },
  "mainEntity": {
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Who can file an MSME Samadhaan claim?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Any micro or small enterprise registered under the MSMED Act that has supplied goods or services to a registered buyer and has not received payment within the agreed terms (or 45 days, whichever is earlier) can file an MSME Samadhaan claim."
        }
      },
      {
        "@type": "Question",
        "name": "What interest is payable on delayed MSME payments?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Under Section 16 of the MSMED Act, the buyer is liable to pay compound interest at three times the bank rate notified by the Reserve Bank of India, compounded monthly, from the appointed day."
        }
      },
      {
        "@type": "Question",
        "name": "How long does MSME Samadhaan resolution take?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The MSEFC is required to dispose of the reference within 90 days, though actual timelines vary depending on the council, the buyer's response, and complexity of the matter."
        }
      }
    ]
  }
};

const PROBLEM = [
  "Delayed payments are common.",
  "Follow-ups don't work.",
  "Commitments get pushed.",
  "Cash flow gets affected.",
  "Most businesses stop at reminders.",
];

const WHAT_WE_DO = [
  "Reviewing your eligibility under MSME provisions",
  "Computing interest on delayed payments",
  "Filing applications on MSME Samadhaan",
  "Structuring documentation and claims",
  "Coordinating with legal professionals where required",
  "Supporting through proceedings and follow-ups",
];

const APPLIES = [
  "You have supplied goods/services to a registered business",
  "Payments are delayed beyond agreed terms",
  "You are registered under MSME",
  "Follow-ups have not resulted in closure",
];

const EXPECT = [
  "Clear understanding of your position",
  "Properly structured claim",
  "End-to-end handling of the process",
  "Support through resolution",
];

export default function MSME() {
  useSEO({
    title:
      "MSME Payment Recovery & Samadhaan Filings | SVMD & Associates",
    description:
      "Recover delayed MSME payments under the MSMED Act. We handle MSME Samadhaan filings, interest computation under Section 16, claim structuring, and end-to-end recovery proceedings for businesses across India.",
    keywords:
      "MSME payment recovery, MSME Samadhaan, delayed payment recovery India, MSMED Act Section 16, MSME interest computation, MSEFC filing, MSME dispute resolution, recover delayed payments from buyers",
    canonical: "/msme-recovery",
    jsonLd: MSME_JSONLD,
  });
  return (
    <>
      {/* HERO (DARK with strong voltage glow) */}
      <section className="relative bg-ink text-paper overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-100 animate-grid-drift" aria-hidden />
        <div className="absolute inset-0 bg-glow-radial" aria-hidden />
        <div className="absolute -right-32 top-20 w-[700px] h-[700px] rounded-full blur-3xl opacity-50 bg-[radial-gradient(circle,rgba(212,255,63,0.22)_0%,transparent_60%)]" aria-hidden />

        <div className="relative max-w-site mx-auto px-6 md:px-12 lg:px-16 pt-28 md:pt-36 pb-20 md:pb-28">
          <Reveal>
            <div className="flex items-center gap-3 text-[12px] text-paper/55 mb-6">
              <Link to="/services" className="hover:text-voltage" data-testid="bc-services">Services</Link>
              <span className="text-paper/30">/</span>
              <Link to="/representation-and-dispute" className="hover:text-voltage" data-testid="bc-rep">Representation</Link>
              <span className="text-paper/30">/</span>
              <span className="text-voltage">MSME Recovery</span>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <h1 className="font-display font-semibold text-[40px] sm:text-[56px] md:text-[72px] lg:text-[84px] leading-[1.04] tracking-[-0.03em] max-w-5xl">
              Payments stuck?<br />
              <span className="text-voltage">We get them moving.</span>
            </h1>
          </Reveal>
          <Reveal delay={200}>
            <p className="mt-7 max-w-2xl text-paper/75 text-base md:text-[18px] leading-[1.55]">
              MSME dues not being cleared? We help businesses recover delayed
              payments under the MSME Act&mdash;including interest claims and
              formal proceedings.
            </p>
          </Reveal>
          <Reveal delay={300}>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <WhatsAppCTA label="Start your MSME claim" testId="msme-hero-whatsapp-cta" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* THE PROBLEM (LIGHT) */}
      <section className="bg-paper text-ink">
        <div className="max-w-site mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28">
          <div className="grid grid-cols-12 gap-6 md:gap-12 mb-10">
            <Reveal className="col-span-12 md:col-span-5">
              <div className="eyebrow mb-4">The problem</div>
              <h2 className="font-display font-semibold text-[32px] md:text-[42px] leading-[1.08] tracking-[-0.025em]">
                A familiar pattern.
              </h2>
            </Reveal>
            <Reveal delay={120} className="col-span-12 md:col-span-7 md:pl-8 md:border-l border-[var(--border-light)] flex items-end">
              <p className="text-[15px] md:text-[16px] text-slate-muted leading-relaxed">
                The MSME Act gives you a structured way to enforce
                recovery&mdash;but it needs to be handled properly.
              </p>
            </Reveal>
          </div>

          <ul className="space-y-3">
            {PROBLEM.map((p, i) => (
              <Reveal key={p} delay={i * 90}>
                <li
                  data-testid={`msme-problem-${i}`}
                  className="flex items-center gap-6 py-5 border-b border-[var(--border-light)] group"
                >
                  <span className="font-mono text-[10px] text-ink-tertiary w-7">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-display text-[20px] md:text-[24px] leading-tight flex-1">
                    {p}
                  </span>
                </li>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      {/* WHAT WE DO (DARK glass) */}
      <section className="relative bg-ink text-paper overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-80" aria-hidden />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(37,99,235,0.2)_0%,transparent_55%)]" aria-hidden />
        <div className="relative max-w-site mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28">
          <Reveal>
            <div className="eyebrow eyebrow-voltage mb-4">What we do</div>
          </Reveal>
          <Reveal delay={80}>
            <h2 className="font-display font-semibold text-[32px] md:text-[42px] leading-[1.08] tracking-[-0.025em] max-w-3xl">
              We handle the entire process.
            </h2>
          </Reveal>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {WHAT_WE_DO.map((w, i) => (
              <Reveal key={w} delay={i * 80}>
                <div
                  data-testid={`msme-do-${i}`}
                  className="glass-panel rounded-xl p-6 h-full flex items-start gap-4"
                >
                  <CheckCircle2 size={20} strokeWidth={1.4} className="text-voltage shrink-0 mt-0.5" />
                  <span className="text-paper/90 text-[14px] md:text-[15px] leading-snug">{w}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHEN THIS APPLIES (LIGHT) */}
      <section className="bg-surface text-ink">
        <div className="max-w-site mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28">
          <Reveal>
            <div className="eyebrow mb-4">When this applies</div>
          </Reveal>
          <Reveal delay={80}>
            <h2 className="font-display font-semibold text-[32px] md:text-[42px] leading-[1.08] tracking-[-0.025em] max-w-3xl">
              A few simple checks.
            </h2>
          </Reveal>

          <ul className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
            {APPLIES.map((a, i) => (
              <Reveal key={a} delay={i * 100}>
                <li
                  data-testid={`msme-applies-${i}`}
                  className="bg-surface-bright rounded-xl p-6 border border-[var(--border-light)] flex items-start gap-4 h-full hover:-translate-y-1 transition-transform"
                >
                  <span className="font-mono text-[10px] text-ink-tertiary mt-1 shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-display text-[17px] md:text-[19px] leading-snug">{a}</span>
                </li>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      {/* WHAT YOU CAN EXPECT (DARK 4 columns) */}
      <section className="relative bg-ink text-paper overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-60" aria-hidden />
        <div className="relative max-w-site mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28">
          <Reveal>
            <div className="eyebrow eyebrow-voltage mb-4">What you can expect</div>
          </Reveal>
          <Reveal delay={80}>
            <h2 className="font-display font-semibold text-[32px] md:text-[42px] leading-[1.08] tracking-[-0.025em] max-w-3xl">
              Clarity, structure, follow-through.
            </h2>
          </Reveal>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {EXPECT.map((e, i) => (
              <Reveal key={e} delay={i * 100}>
                <div
                  data-testid={`msme-expect-${i}`}
                  className="glass-panel rounded-xl p-6 h-full"
                >
                  <div className="font-mono text-[10px] tracking-[0.18em] text-voltage">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <h3 className="mt-3 font-display text-[18px] md:text-[20px] leading-snug">
                    {e}
                  </h3>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SIMPLE START (DARK gradient) */}
      <section className="relative bg-hero-gradient text-paper overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-40" aria-hidden />
        <div className="absolute -bottom-32 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full blur-3xl opacity-50 bg-[radial-gradient(circle,rgba(212,255,63,0.18)_0%,transparent_60%)]" aria-hidden />
        <div className="relative max-w-site mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28">
          <Reveal>
            <div className="eyebrow eyebrow-voltage mb-4">Simple start</div>
          </Reveal>
          <Reveal delay={80}>
            <h2 className="font-display font-semibold text-[36px] md:text-[52px] lg:text-[64px] leading-[1.06] tracking-[-0.025em] max-w-4xl">
              You don&rsquo;t need to figure everything out first.
            </h2>
          </Reveal>
          <Reveal delay={180}>
            <p className="mt-6 max-w-2xl text-paper/75 text-base md:text-[17px] leading-relaxed">
              Share your details. We&rsquo;ll review and guide you on the next
              steps.
            </p>
          </Reveal>
          <Reveal delay={260}>
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <WhatsAppCTA label="Share details on WhatsApp" testId="msme-close-whatsapp-cta" />
              <PhoneIconBtn variant="dark" testId="msme-close-phone-icon" />
              <MailIconBtn variant="dark" testId="msme-close-email-icon" />
            </div>
          </Reveal>

          <Reveal delay={360}>
            <div className="mt-10">
              <Link
                to="/representation-and-dispute"
                data-testid="msme-back-rep"
                className="link-arrow text-paper/65 hover:text-voltage text-[13px]"
              >
                <span className="link-wipe">Back to Representation &amp; Dispute</span>
                <ArrowRight size={14} strokeWidth={2} className="arrow" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
